import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, GitCompare, ArrowUpRight, Eye } from "lucide-react";
import { imageUrl, whatsappLink } from "../data/constants";
import { ProductPlaceholder } from "./ProductPlaceholder";
import { ColourSwatch } from "./ColourSwatch";
import { EnquiryDialog } from "./EnquiryDialog";
import { QuickViewModal } from "./QuickViewModal";
import { useCompare } from "../context/CompareContext";

export function ProductCard({ product, palette, minimal = false }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const { toggle, has } = useCompare();
  const img = imageUrl(product.primary_image || product.images?.[0], 600);

  return (
    <>
      <div
        data-testid="product-card-item"
        className="group flex animate-fade-up flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-[#0B1B3D]/10"
      >
        <div className="relative aspect-square overflow-hidden bg-white">
          <Link to={`/product/${product.id}`} className="absolute inset-0 block">
            {img ? (
              <img src={img} alt={product.name} loading="lazy" decoding="async"
                className="h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-105" />
            ) : (
              <ProductPlaceholder className="h-full w-full" />
            )}
          </Link>
          <span className="absolute left-3 top-3 rounded-full bg-[#0B1B3D]/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur">
            {product.category}
          </span>
          <button
            onClick={() => setQuickOpen(true)}
            data-testid="product-quick-view-btn"
            className="absolute bottom-3 left-1/2 flex -translate-x-1/2 translate-y-14 items-center gap-1.5 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-[#0B1B3D] shadow-lg backdrop-blur transition-all duration-300 group-hover:translate-y-0 hover:bg-[#ECFDF5] hover:text-[#059669]"
          >
            <Eye className="h-4 w-4" /> Quick View
          </button>
        </div>

        <div className="flex flex-1 flex-col p-4">
          <Link to={`/product/${product.id}`}>
            <h3 className="font-display text-lg font-semibold text-[#0B1B3D] transition-colors group-hover:text-[#059669]">
              {product.name}
            </h3>
          </Link>

          {!minimal && (
            <>
              {product.colours?.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {product.colours.slice(0, 6).map((c) => (
                    <ColourSwatch key={c} name={c} palette={palette} size="sm" />
                  ))}
                  {product.colours.length > 6 && (
                    <span className="text-[11px] font-medium text-slate-400">+{product.colours.length - 6}</span>
                  )}
                </div>
              )}
              {product.features?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {product.features.slice(0, 3).map((f) => (
                    <span key={f} className="rounded-full bg-[#ECFDF5] px-2.5 py-0.5 text-[11px] font-medium text-[#059669] ring-1 ring-[#A7F3D0]">
                      {f}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="mt-auto flex items-center gap-2 pt-4">
            <button
              onClick={() => setQuoteOpen(true)}
              data-testid="product-quick-quote-btn"
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#0B1B3D] px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1E293B]"
            >
              Get Quote <ArrowUpRight className="h-4 w-4" />
            </button>
            <a
              href={whatsappLink({ product: product.name, size: product.sizes?.[0], colour: product.colours?.[0] })}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-card-whatsapp-btn"
              title="WhatsApp"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#25D366] text-white transition-transform hover:scale-105"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
            {!minimal && (
              <button
                onClick={() => toggle(product)}
                data-testid="product-compare-checkbox"
                title="Compare"
                className={`flex h-10 w-10 items-center justify-center rounded-xl border transition-colors ${
                  has(product.id)
                    ? "border-[#10B981] bg-[#ECFDF5] text-[#059669]"
                    : "border-slate-200 text-slate-500 hover:border-[#10B981] hover:text-[#059669]"
                }`}
              >
                <GitCompare className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      <EnquiryDialog open={quoteOpen} onOpenChange={setQuoteOpen} product={product} />
      <QuickViewModal product={product} palette={palette} open={quickOpen} onOpenChange={setQuickOpen} />
    </>
  );
}
